import React, { Fragment, useState, useEffect } from 'react';
import { compose } from 'recompose';
import { reduxForm, getFormValues, Field } from 'redux-form';
import { connect } from 'react-redux';

import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { TextInput, SelectInput, CardElement, AddButton, Modal, StatusChip, EmptyState } from '../../_Components';
import { GET_ONE, provider } from '../../_utils';

const toCrmFields = (payload) => {
    if (!payload || payload.aborted || !payload.data) return [];
    return Object.keys(payload.data).map((key) => {
        const item = payload.data[key] || {};
        return { id: key, name: item.title !== key ? item.title : item.formLabel };
    });
};

const isBitrixProject = (project) => (
    !!(project && project.settings && project.settings.integration && project.settings.integration.type === 'Bitrix')
);

const Modules = [
    { id: 'lead', name: 'Лиды' },
    { id: 'contact', name: 'Контакт' },
    { id: 'deal', name: 'Сделка' },
];

const crmOptions = (module, leadFields, contactFields, dealFields) => {
    if (module === 'contact') return contactFields || [];
    if (module === 'deal' || module === 'company') return dealFields || [];
    return leadFields || [];
};

const crmName = (module, crm, leadFields, contactFields, dealFields) => {
    const list = crmOptions(module, leadFields, contactFields, dealFields);
    const item = (list || []).find((entry) => entry && entry.id === crm);
    return item ? item.name : (crm || '—');
};

const fieldLabel = (fields, value) => {
    const item = (fields || []).find((entry) => entry.value === value);
    return item ? item.label : value;
};

const Edit = ({ index, fields = [], formValues, leadFields, contactFields, dealFields }) => {
    const isForm = index >= 0;
    const module = isForm
        ? (formValues && formValues.integration && formValues.integration[index] && formValues.integration[index].module)
        : (formValues && formValues.module);
    const fieldsArray = (fields || []).map((item) => ({ id: item.value, name: item.label }));

    return (
        <Fragment>
            <div className="pc-fgrid">
                <Field
                    name={isForm ? `integration[${index}].field` : 'field'}
                    required={true}
                    options={fieldsArray}
                    component={SelectInput}
                    label="Поле карточки"
                />
                <Field
                    name={isForm ? `integration[${index}].module` : 'module'}
                    required={true}
                    options={Modules}
                    component={SelectInput}
                    label="Модуль"
                />
                <Field
                    name={isForm ? `integration[${index}].crm` : 'crm'}
                    required={true}
                    options={crmOptions(module, leadFields, contactFields, dealFields)}
                    component={SelectInput}
                    label="Поле в CRM"
                    showValue
                />
                <Field
                    name={isForm ? `integration[${index}].default` : 'default'}
                    component={TextInput}
                    label="Значение по умолчанию"
                />
            </div>
        </Fragment>
    );
};

const Integration = ({ formValues, change, project }) => {
    const [open, setOpen] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [leadFields, setLeadFields] = useState(null);
    const [dealFields, setDealFields] = useState(null);
    const [contactFields, setContactFields] = useState(null);
    const projectId = project && project._id;
    const bitrix = isBitrixProject(project);

    useEffect(() => {
        if (!projectId || !bitrix) return undefined;
        let alive = true;
        const load = async () => {
            const [leads, deals, contacts] = await Promise.all([
                provider(GET_ONE, '/bitrix/fields/lead', projectId),
                provider(GET_ONE, '/bitrix/fields/deal', projectId),
                provider(GET_ONE, '/bitrix/fields/contact', projectId),
            ]);
            if (!alive) return;
            setLeadFields(toCrmFields(leads));
            setDealFields(toCrmFields(deals));
            setContactFields(toCrmFields(contacts));
        };
        load();
        return () => { alive = false; };
    }, [projectId, bitrix]);

    if (!formValues || !project) {
        return null;
    }

    const fields = project.fields || [];
    const integration = formValues.integration || [];

    const closeEdit = () => {
        setOpen(false);
        setEditIndex(null);
    };

    const openCreate = () => {
        setEditIndex(null);
        setOpen(true);
    };

    const openRow = (index) => {
        setEditIndex(index);
        setOpen(true);
    };

    const save = (form) => {
        if (!form) return;
        const next = [...(formValues.integration || [])];
        if (editIndex != null && next[editIndex]) {
            next[editIndex] = { ...next[editIndex], ...form };
        } else {
            next.push(form);
        }
        change('integration', next);
        closeEdit();
    };

    const remove = (index, event) => {
        if (event) event.stopPropagation();
        change('integration', integration.filter((_, i) => i !== index));
        if (editIndex === index) closeEdit();
    };

    return (
        <div className="pc-fill" style={{ overflow: 'auto' }}>
            <CardElement
                title="Передача полей во внешнюю систему"
                subheader={<StatusChip tone="mono">{integration.length}</StatusChip>}
                action={<AddButton handleAddClick={openCreate} label="Добавить правило" variant="contained" />}
                style={{ maxWidth: 920 }}
                content={(
                    <div>
                        <div className="pc-sect" style={{ paddingBottom: 0, borderTop: 0 }}>
                            <div className="pc-note pc-note--in">
                                <InfoOutlinedIcon sx={{ fontSize: 16, mt: '1px' }} />
                                <span>
                                    Здесь настраивается передача только для этой карточки. Общее сопоставление полей проекта живёт во вкладке «Поля» — правила отсюда его перекрывают.
                                </span>
                            </div>
                        </div>
                        <div className="pc-tblwrap">
                            <table className="pc-cards-tbl">
                                <thead>
                                    <tr>
                                        <th>Поле карточки</th>
                                        <th style={{ width: 150 }}>Модуль</th>
                                        <th style={{ width: 190 }}>Поле в CRM</th>
                                        <th style={{ width: 170 }}>Значение по умолчанию</th>
                                        <th style={{ width: 44 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {integration.map((row, index) => (
                                        <tr
                                            key={index}
                                            className={editIndex === index ? 'is-cur' : ''}
                                            onClick={() => openRow(index)}
                                        >
                                            <td>
                                                {fieldLabel(fields, row.field)}
                                                {row.field ? <span className="src">{row.field}</span> : null}
                                            </td>
                                            <td>{(Modules.find((item) => item.id === row.module) || {}).name || row.module}</td>
                                            <td>
                                                {crmName(row.module, row.crm, leadFields, contactFields, dealFields)}
                                                {row.crm ? <span className="src">{row.crm}</span> : null}
                                            </td>
                                            <td style={{ color: row.default ? 'inherit' : 'var(--muted-2)' }}>{row.default || '—'}</td>
                                            <td className="act">
                                                <IconButton size="small" onClick={(event) => remove(index, event)}>
                                                    <CloseIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {integration.length === 0 && (
                                <EmptyState
                                    title="Правил пока нет"
                                    description="Добавьте правило, чтобы значение из карточки попадало в конкретное поле Bitrix."
                                />
                            )}
                        </div>
                    </div>
                )}
            />
            {open && (
                <Modal
                    width={560}
                    title={editIndex != null ? 'Правило интеграции' : 'Добавить правило'}
                    open={open}
                    handleSaveClick={save}
                    handleCloseClick={closeEdit}
                    fields={
                        <Edit
                            fields={fields}
                            leadFields={leadFields}
                            contactFields={contactFields}
                            dealFields={dealFields}
                        />
                    }
                    initial={editIndex != null ? integration[editIndex] : { module: 'lead' }}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    const formValues = getFormValues('card')(state);
    const { fetch } = state;
    return {
        formValues,
        isLoading: !!fetch.loading
    };
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'card',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    }),
);

export default enhance(Integration);
